import { CheckCircleOutlined } from '@ant-design/icons';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Modal, Row } from 'antd';
import useCheckNotificationsPermissions from 'hooks/useCheckNotificationsPermissions';
import useReminderState from 'hooks/useReminderState';
import useSchedulePeriodNotifications from 'hooks/useSchedulePeriodNotifications';
import useShouldAskForPeriod from 'hooks/useShouldAskForPeriod';
import useSymptomTrackingData from 'hooks/useSymptomTrackingData';
import ActionTypes, { NotificationIds } from 'notifications';
import { getTrackingNotificationChannelId } from 'notifications/channels';
import posthog from 'posthog-js';

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import Picker from 'react-mobile-picker';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from 'store/users/selectors';
import Platform from 'utils/Platform';
import NotificationSuggester from '../cards/NotificationSuggester';
import { Button } from 'components/ui';
import { colors } from 'design';

export type SymptomTrackingReminderModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onReminderEnabled?: () => void;
  hidePeriodReminder?: boolean;
  hideDailyReminder?: boolean;
};

const useStyles = createUseStyles({
  periodReminderForm: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: 32,

    '& h3': {
      marginBottom: 16,
      '& svg': {
        marginLeft: 2,
      },
    },
  },
  reminderTimeQuestion: {
    margin: '0 0 8px',
    textAlign: 'center',
  },
  notificationScheduleForm: {
    justifyContent: 'space-around',

    '& .ant-alert': {
      marginBottom: 16,
    },
  },
  reminderTimePicker: {
    width: 200,
  },
});

const SymptomTrackingReminderModal: React.FC<SymptomTrackingReminderModalProps> = ({
  isOpen,
  onClose,
  onReminderEnabled,
  hidePeriodReminder = false,
  hideDailyReminder = false,
}) => {
  const styles = useStyles();
  const { t } = useTranslation();
  const currentUser = useSelector(selectCurrentUser);
  const { doesUserHavePushNotifications } = useCheckNotificationsPermissions();
  const { probableNextPeriodDates } = useSymptomTrackingData();
  const { schedulePeriodReminder, cancelPeriodReminders } = useSchedulePeriodNotifications();

  const { isPeriodTrackingEnabled } = useShouldAskForPeriod();

  const timePickerSelections: { hour: number[]; minute: number[] } = {
    hour: Array.from({ length: 24 }, (_, i) => i),
    minute: Array.from({ length: 12 }, (_, i) => i * 5),
  };

  const [timePickerValue, setTimePickerValue] = useState({ hour: 21, minute: 0 });

  const {
    reminderTime,
    isPeriodReminderEnabled,
    areDailyNotificationsEnabled,
    setReminderTime,
    clearReminderTime,
    setPeriodReminderEnabled,
    clearPeriodReminderEnabled,
  } = useReminderState();

  useEffect(() => {
    if (reminderTime) {
      setTimePickerValue(reminderTime);
    }
  }, [reminderTime]);

  const disableDailyReminder = () => {
    LocalNotifications.getPending().then(pending =>
      LocalNotifications.cancel({
        notifications: pending.notifications.filter(n => n.id === NotificationIds.SYMPTOM_TRACKING_REMINDER),
      }),
    );
    clearReminderTime();

    posthog.capture('tracking.reminder_disabled');
  };

  const disablePeriodReminder = () => {
    clearPeriodReminderEnabled();
    cancelPeriodReminders();
    posthog.capture('tracking.period_reminder.disabled');
  };

  const useEnhancedNotifications =
    Platform.getPlatform() === 'ios' && posthog.getFeatureFlag('ios-notifications-better-settings') === true;

  const enableDailyReminder = () => {
    const channelId = getTrackingNotificationChannelId();
    LocalNotifications.schedule({
      notifications: [
        {
          title: t('appName'),
          body: t('symptomTracking.notification.dailyReminder', { name: currentUser?.first_name }),
          id: NotificationIds.SYMPTOM_TRACKING_REMINDER,
          schedule: { on: { hour: timePickerValue.hour, minute: timePickerValue.minute }, allowWhileIdle: true },
          actionTypeId: ActionTypes.SYMPTOM_TRACKING_REMINDER,
          ...(channelId ? { channelId } : {}),
          ...(useEnhancedNotifications ? { sound: 'default' } : {}),
        },
      ],
    });

    setReminderTime(timePickerValue);
    posthog.capture('tracking.reminder_enabled_or_changed', {
      time: timePickerValue,
    });

    if (onReminderEnabled) {
      onReminderEnabled();
    }
  };

  const enablePeriodReminder = () => {
    setPeriodReminderEnabled(true);
    if (probableNextPeriodDates) {
      schedulePeriodReminder(probableNextPeriodDates.start, areDailyNotificationsEnabled);
    }
    posthog.capture('tracking.period_reminder.enabled', { source: 'reminder_modal' });
  };

  return (
    <Modal open={isOpen} onCancel={onClose} footer={<span />}>
      {Platform.isNative() ? (
        <>
          {!hidePeriodReminder && isPeriodTrackingEnabled && (
            <Row className={styles.periodReminderForm}>
              {doesUserHavePushNotifications ? (
                isPeriodReminderEnabled ? (
                  <>
                    <h3>
                      {t('symptomTracking.reminder.periodReminder')}{' '}
                      <span style={{ color: colors.brand.accentStrong }}>
                        activé <CheckCircleOutlined />
                      </span>
                    </h3>
                    <p className={styles.reminderTimeQuestion}>{t('symptomTracking.periodReminder.description')}</p>
                    <Button variant="danger" onClick={() => disablePeriodReminder()}>
                      {t('symptomTracking.reminder.disable')}
                    </Button>
                  </>
                ) : (
                  <>
                    <h3>
                      {t('symptomTracking.reminder.periodReminder')}{' '}
                      <span style={{ color: colors.text.disabled }}>
                        {t('symptomTracking.periodReminder.disabled')}
                      </span>
                    </h3>
                    <p className={styles.reminderTimeQuestion}>{t('symptomTracking.periodReminder.description')}</p>
                    <Button variant="primary" onClick={() => enablePeriodReminder()}>
                      {t('symptomTracking.reminder.enable')}
                    </Button>
                  </>
                )
              ) : (
                <>
                  <h3>{t('symptomTracking.reminder.periodReminder')}</h3>
                  <p className={styles.reminderTimeQuestion}>{t('symptomTracking.periodReminder.description')}</p>
                  <NotificationSuggester text={t('symptomTracking.reminder.enableNotifications')} />
                </>
              )}
            </Row>
          )}

          {!hideDailyReminder && (
            <>
              <Row className={styles.notificationScheduleForm}>
                <h3>{t('symptomTracking.reminder.dailyReminder')}</h3>
                <p className={styles.reminderTimeQuestion}>{t('symptomTracking.reminder.setTimeQuestion')}</p>
                <Picker className={styles.reminderTimePicker} value={timePickerValue} onChange={setTimePickerValue}>
                  <Picker.Column key="hour" name="hour">
                    {timePickerSelections.hour.map((option: number) => (
                      <Picker.Item key={`hour-${option}`} value={option}>
                        {option.toString().padStart(2, '0')}
                      </Picker.Item>
                    ))}
                  </Picker.Column>
                  <Picker.Column key="minute" name="minute">
                    {timePickerSelections.minute.map((option: number) => (
                      <Picker.Item key={`minute-${option}`} value={option}>
                        {option.toString().padStart(2, '0')}
                      </Picker.Item>
                    ))}
                  </Picker.Column>
                </Picker>
              </Row>
              <Row className={styles.notificationScheduleForm}>
                {!doesUserHavePushNotifications && (
                  <NotificationSuggester text={t('symptomTracking.reminder.enableNotifications')} />
                )}
              </Row>
              <Row className={styles.notificationScheduleForm}>
                {areDailyNotificationsEnabled ? (
                  <Button variant="danger" onClick={() => disableDailyReminder()}>
                    {t('symptomTracking.reminder.disable')}
                  </Button>
                ) : (
                  <Button variant="primary" disabled={!doesUserHavePushNotifications} onClick={enableDailyReminder}>
                    {t('symptomTracking.reminder.enable')}
                  </Button>
                )}
                {areDailyNotificationsEnabled && (
                  <Button
                    variant="primary"
                    onClick={enableDailyReminder}
                    disabled={
                      !reminderTime ||
                      (timePickerValue.hour === reminderTime.hour && timePickerValue.minute === reminderTime.minute)
                    }
                  >
                    {t('symptomTracking.reminder.change')}
                  </Button>
                )}
              </Row>
            </>
          )}
        </>
      ) : (
        <div>{t('symptomTracking.reminder.mobileOnly')}</div>
      )}
    </Modal>
  );
};

export default SymptomTrackingReminderModal;
